import { KButton, KDialog, KIcon } from "@kyburz/kdesign-system";
import axios, { CancelTokenSource } from "axios";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { BillingTariffInterface } from "../../../interfaces/BillingTarifInterface";
import { BillingColumns } from "./typings";
import useSetState from "../../../hooks/useTypedSetState";
import { setAlert } from "../../../redux/actions/actions";
import {
  deleteBillingTariff,
  getBillingTariffs,
} from "../../../apiCalls/admin/billing_tariffs";
import TableDropdown from "../../../components/TableDropdown";
import NewBillingTariff from "./NewBillingTariff/NewBillingTariff";
import { simStatuses, simTariffs } from "./constants";
import KInfiniteTable from "../../../components/table/KInfiniteTable";
import { processTariff } from "./dataHandlers";
import EditBillingTariff from "./EditBillingTariff/EditBillingTariff";
import {useStyles} from "./styles";

const defaultSortingRules = {
  id: "name",
  order: "asc",
};

enum ModalType {
  Edit = "edit",
  Create = "create",
  Delete = "delete",
}

const InternalBillingTariffs = () => {
  const company = useSelector(
    (state: any) => state.customer.currentCustomer?.shortname,
  );
  const { CancelToken } = axios;
  const dispatch = useDispatch();
  const classes = useStyles();

  const [
    {
      items,
      itemsCount,
      cancelTokenSource,
      hasNextPage,
      isNextPageLoading,
      pagesLoaded,
      order,
      filterBy,

      modal,
      activeTariff,

      reload,
      loading,
    },
    setState,
  ] = useSetState({
    items: [] as BillingTariffInterface[],
    itemsCount: 0,
    cancelTokenSource: null as CancelTokenSource | null,
    hasNextPage: true,
    isNextPageLoading: false,
    pagesLoaded: 0,
    order: defaultSortingRules,
    filterBy: "",

    modal: null as ModalType | null,
    activeTariff: null as BillingTariffInterface | null,

    reload: false,
    loading: false,
  });

  const fetchTariffs = (
    page: number,
    refresh: boolean = true,
    filter: string = "",
  ): void => {
    if (cancelTokenSource) {
      cancelTokenSource.cancel("Search cancelled");
    }
    const source = CancelToken.source();
    const sourceToken = source.token;
    setState({
      isNextPageLoading: true,
      cancelTokenSource: source,
      loading: true,
    });
    const previousArray = refresh ? [] : items;
    getBillingTariffs(page, filter, sourceToken, order.id, order.order)
      .then((res) => {
        if (!res || !res?.data) return;
        const {
          data: { data },
          hasNextPage: resHasNextPage,
          count,
        } = res;
        const data_per_company = data.map((x) => processTariff(company, x));
        setState({
          items: [...previousArray, ...data_per_company],
          itemsCount: count,
          isNextPageLoading: false,
          pagesLoaded: page,
          loading: false,
          hasNextPage: resHasNextPage,
        });
      })
      .finally(() => {
        setState({
          isNextPageLoading: false,
        });
      });
  };

  useEffect(() => fetchTariffs(1, true, filterBy), [filterBy, order]);

  useEffect(() => {
    if (reload) {
      setState({ reload: false });
      fetchTariffs(1, true, filterBy);
    }
  }, [reload]);

  const loadNextPage = () => fetchTariffs(pagesLoaded + 1, false, filterBy);

  const filterTable = (values: { id: string; value: string }[]): void => {
    const parsedFilter: string = values
      .map((x) => `${x.id}:regex:${x.value}`)
      .join(";");
    setState({ filterBy: parsedFilter });
  };

  const sortTable = (data) => {
    const rules = data[0];
    if (rules) {
      setState({
        order: {
          id: rules.id,
          order: rules.desc ? "desc" : "asc",
        },
      });
    }
  };

  const onModalClose = (withReload = false) =>
    setState({ modal: null, reload: withReload });

  const removeTariff = () => {
    deleteBillingTariff(activeTariff?._id)
      .then((res) => {
        if (res?.data) {
          dispatch(
            setAlert(
              `Removed tariff ${activeTariff?.companyData?.name}`,
              "success",
            ),
          );
          setState({ reload: true });
        } else {
          dispatch(
            setAlert(
              `Error removing tariff ${activeTariff?.companyData?.name}`,
              "error",
            ),
          );
        }
      })
      .finally(onModalClose);
  };

  const renderTableActions = (row: BillingTariffInterface) => (
    <TableDropdown
      menuItems={[
        {
          icon: "edit",
          title: "Edit",
          action: () => setState({ modal: ModalType.Edit, activeTariff: row }),
        },
        {
          icon: "trash-2",
          title: "Delete",
          action: () =>
            setState({ modal: ModalType.Delete, activeTariff: row }),
        },
      ]}
    />
  );

  const columns = React.useMemo(
    () => [
      {
        id: BillingColumns.license,
        Header: "License",
        accessor: "license",
        defaultCanSort: true,
      },
      {
        id: BillingColumns.name,
        Header: "Name",
        accessor: "companyData.name",
        defaultCanSort: true,
        disableFilters: false,
      },
      {
        id: BillingColumns.cost,
        Header: "Cost",
        accessor: (tariff: BillingTariffInterface) =>
          Number(tariff?.companyData?.cost).toFixed(2),
        defaultCanSort: true,
        disableFilters: false,
      },
      {
        id: BillingColumns.simTariff,
        Header: "SIM Tariff (for Telefonica API)",
        accessor: (tariff: BillingTariffInterface) =>
          simTariffs.find(
            ({ value }) => value === tariff?.companyData?.sim_tariff,
          )?.label || "n.a.",
        defaultCanSort: true,
        disableFilters: false,
      },
      {
        id: BillingColumns.simStatus,
        Header: "SIM Status (for Telefonica API)",
        accessor: (tariff: BillingTariffInterface) =>
          simStatuses.find(
            ({ value }) => value === tariff?.companyData?.sim_status,
          )?.label || "n.a.",
      },
      {
        id: BillingColumns.actions,
        Header: "",
        accessor: "",
        width: 50,
        Cell: (row) => renderTableActions(row.row.original),
        disableFilters: true,
        sticky: "right",
      },
    ],
    [],
  );

  const deletePopup = (
    <KDialog
      size="xs"
      title="Delete tariff"
      open={modal === ModalType.Delete}
      primaryCTAonClick={removeTariff}
      primaryCTAColor="danger"
      primaryCTAText="Delete"
      secondaryCTAonClick={() => onModalClose(false)}
      secondaryCTAText="Cancel"
    >
      <p>
        Are you sure you want delete tariff {activeTariff?.companyData?.name}?
      </p>
    </KDialog>
  );

  const editPopup = activeTariff ? (
    <EditBillingTariff
      open={modal === ModalType.Edit}
      tariff={activeTariff}
      handleClose={onModalClose}
    />
  ) : (
    <div />
  );

  return (
    <div className={classes.container}>
      <div className={classes.buttonContainer}>
        <KButton
          icon={<KIcon icon="plus" />}
          onClick={() => setState({ modal: ModalType.Create })}
        >
          Create Tariff
        </KButton>
      </div>

      <div className={classes.tableContainer}>
        <KInfiniteTable
          data={items}
          hasNextPage={hasNextPage}
          isNextPageLoading={isNextPageLoading}
          loadNextPage={loadNextPage}
          columns={columns}
          manualSortBy={true}
          initialSortBy={[defaultSortingRules]}
          sortTable={sortTable}
          hasStickyColumns={true}
          hasFilter
          manualFilters
          filterTable={filterTable}
          loading={loading}
        />
      </div>

      <NewBillingTariff
        open={modal === ModalType.Create}
        handleClose={onModalClose}
        itemsCount={itemsCount}
      />
      {editPopup}
      {deletePopup}
    </div>
  );
};

export default InternalBillingTariffs;
